import { useEffect, useRef, useState, useCallback } from "react";
import { X, ZoomIn, ZoomOut, RotateCcw, Maximize2 } from "lucide-react";

/**
 * Fullscreen, accessible flyer viewer.
 * - Mouse wheel zoom (clamped 1x → 5x).
 * - Click & drag to pan when zoomed.
 * - Pinch-to-zoom + drag on touch devices (single & two-finger gestures).
 * - Double-click / double-tap toggles 1x ↔ 2.5x.
 * - Esc / backdrop click / close button to dismiss.
 */
export default function FlyerLightbox({ src, alt = "", onClose }) {
    const [zoom, setZoom] = useState(1);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const containerRef = useRef(null);
    const dragStartRef = useRef(null);
    const pinchStartRef = useRef(null);

    const reset = useCallback(() => { setZoom(1); setPan({ x: 0, y: 0 }); }, []);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") onClose?.();
            else if (e.key === "+" || e.key === "=") setZoom((z) => Math.min(5, z * 1.2));
            else if (e.key === "-") setZoom((z) => Math.max(1, z / 1.2));
            else if (e.key === "0") reset();
        };
        window.addEventListener("keydown", onKey);
        // Prevent body scroll while open.
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = prev;
        };
    }, [onClose, reset]);

    const onWheel = (e) => {
        e.preventDefault();
        const delta = -Math.sign(e.deltaY) * 0.15;
        setZoom((z) => Math.min(5, Math.max(1, z + delta)));
    };

    const onMouseDown = (e) => {
        if (zoom <= 1) return;
        dragStartRef.current = { x: e.clientX - pan.x, y: e.clientY - pan.y };
    };
    const onMouseMove = (e) => {
        if (!dragStartRef.current) return;
        setPan({ x: e.clientX - dragStartRef.current.x,
                 y: e.clientY - dragStartRef.current.y });
    };
    const onMouseUp = () => { dragStartRef.current = null; };

    const dist = (a, b) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);

    const onTouchStart = (e) => {
        if (e.touches.length === 2) {
            pinchStartRef.current = {
                d: dist(e.touches[0], e.touches[1]),
                z: zoom,
            };
        } else if (e.touches.length === 1 && zoom > 1) {
            dragStartRef.current = {
                x: e.touches[0].clientX - pan.x,
                y: e.touches[0].clientY - pan.y,
            };
        }
    };
    const onTouchMove = (e) => {
        if (e.touches.length === 2 && pinchStartRef.current) {
            e.preventDefault();
            const d = dist(e.touches[0], e.touches[1]);
            const ratio = d / pinchStartRef.current.d;
            setZoom(Math.min(5, Math.max(1, pinchStartRef.current.z * ratio)));
        } else if (e.touches.length === 1 && dragStartRef.current) {
            setPan({
                x: e.touches[0].clientX - dragStartRef.current.x,
                y: e.touches[0].clientY - dragStartRef.current.y,
            });
        }
    };
    const onTouchEnd = () => {
        pinchStartRef.current = null;
        dragStartRef.current = null;
    };

    const onDoubleClick = (e) => {
        e.preventDefault();
        if (zoom > 1.1) reset();
        else setZoom(2.5);
    };

    return (
        <div
            data-testid="flyer-lightbox"
            className="fixed inset-0 z-[120] bg-black/95 flex items-center justify-center select-none"
            onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
            {/* Top toolbar */}
            <div className="absolute top-0 left-0 right-0 flex items-center justify-between gap-2 p-3 z-10 text-white">
                <span className="text-xs font-bold opacity-80 truncate max-w-[60%]">{alt}</span>
                <div className="flex items-center gap-1">
                    <button onClick={() => setZoom((z) => Math.max(1, z / 1.25))}
                        className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
                        data-testid="flyer-lightbox-zoom-out" aria-label="Zoom -">
                        <ZoomOut size={16} />
                    </button>
                    <button onClick={() => setZoom((z) => Math.min(5, z * 1.25))}
                        className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
                        data-testid="flyer-lightbox-zoom-in" aria-label="Zoom +">
                        <ZoomIn size={16} />
                    </button>
                    <button onClick={reset}
                        className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
                        data-testid="flyer-lightbox-reset" aria-label="Réinitialiser">
                        <RotateCcw size={16} />
                    </button>
                    <button onClick={onClose}
                        className="ml-2 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center"
                        data-testid="flyer-lightbox-close" aria-label="Fermer">
                        <X size={18} />
                    </button>
                </div>
            </div>

            <div
                ref={containerRef}
                className="absolute inset-0 flex items-center justify-center overflow-hidden"
                onWheel={onWheel}
                onMouseDown={onMouseDown}
                onMouseMove={onMouseMove}
                onMouseUp={onMouseUp}
                onMouseLeave={onMouseUp}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
                onDoubleClick={onDoubleClick}
                style={{
                    cursor: zoom > 1 ? (dragStartRef.current ? "grabbing" : "grab") : "zoom-in",
                    touchAction: "none",
                }}>
                <img
                    src={src}
                    alt={alt}
                    draggable={false}
                    data-testid="flyer-lightbox-image"
                    style={{
                        maxWidth: "95vw",
                        maxHeight: "92vh",
                        transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                        transition: dragStartRef.current || pinchStartRef.current
                            ? "none" : "transform 0.18s ease-out",
                    }}
                />
            </div>

            <div className="absolute bottom-4 left-0 right-0 text-center text-white/70 text-xs flex items-center justify-center gap-2 pointer-events-none">
                <Maximize2 size={11} />
                <span>Molette / pincer pour zoomer · double-clic pour réinitialiser · Échap pour fermer · {Math.round(zoom * 100)} %</span>
            </div>
        </div>
    );
}
